import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { GraduationCap, Filter, Lock, ChevronRight, FileText, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { endpoints } from "@/lib/api";

const TRAINING_TONE = {
  Valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Expiring: "bg-amber-50 text-amber-700 border-amber-200",
  Expired: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function TrainingsCatalogPage() {
  const [params, setParams] = useSearchParams();
  const [catalog, setCatalog] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientSlug, setClientSlug] = useState(params.get("client") || "all");
  const [authorized, setAuthorized] = useState(null);
  const [drawer, setDrawer] = useState({ open: false, item: null });
  const navigate = useNavigate();

  useEffect(() => {
    endpoints.me().then((u) => endpoints.listConsultants().then((cs) => {
      const me = cs.find((c) => c.name === u.name);
      setAuthorized(u.canonical_role === "admin" || u.canonical_role === "staff" || !!me?.is_manager);
    }));
    endpoints.listClients().then(setClients).catch(() => {});
  }, []);
  useEffect(() => {
    if (!authorized) return;
    const p = {}; if (clientSlug !== "all") p.client_slug = clientSlug;
    endpoints.trainingsCatalog(p).then(setCatalog).catch(() => {});
    const n = new URLSearchParams(params);
    if (clientSlug === "all") n.delete("client"); else n.set("client", clientSlug);
    setParams(n, { replace: true });
    // eslint-disable-next-line
  }, [authorized, clientSlug]);

  if (authorized === null) return <div className="p-8 text-sm text-slate-500">Loading...</div>;
  if (!authorized) return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto"><div className="glass-card rounded-3xl p-10 text-center">
      <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4"><Lock className="h-5 w-5" /></div>
      <h1 className="font-display text-xl font-extrabold mb-1">Authorized staff only</h1>
    </div></div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md shadow-emerald-500/25"><GraduationCap className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Training Catalog</h1>
            <p className="text-xs text-slate-500">{catalog.length} unique trainings across employees</p>
          </div>
        </div>
        <div className="ml-auto">
          <Select value={clientSlug} onValueChange={setClientSlug}>
            <SelectTrigger data-testid="train-client-filter" className="h-9 w-[220px] text-xs"><Filter className="h-3.5 w-3.5 mr-1 text-slate-400" /><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All clients</SelectItem>{clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {catalog.length === 0 ? (
        <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">No training records yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {catalog.map((t) => (
            <button
              key={t.name}
              data-testid={`train-card-${t.name.replace(/\s+/g, "-")}`}
              onClick={() => setDrawer({ open: true, item: t })}
              className="glass-card rounded-3xl p-5 text-left card-hover group"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-sm truncate">{t.name}</h3>
                  <div className="text-[11px] text-slate-500 mt-0.5">{t.holders.length} holder{t.holders.length !== 1 ? "s" : ""}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {t.valid > 0 && <Badge variant="outline" className={`text-[10px] rounded-full ${TRAINING_TONE.Valid}`}>{t.valid} valid</Badge>}
                {t.expiring > 0 && <Badge variant="outline" className={`text-[10px] rounded-full ${TRAINING_TONE.Expiring}`}>{t.expiring} expiring</Badge>}
                {t.expired > 0 && <Badge variant="outline" className={`text-[10px] rounded-full ${TRAINING_TONE.Expired}`}>{t.expired} expired</Badge>}
              </div>
            </button>
          ))}
        </div>
      )}

      <Sheet open={drawer.open} onOpenChange={(o) => setDrawer({ open: o, item: o ? drawer.item : null })}>
        <SheetContent data-testid="drawer-training-holders" className="sm:max-w-lg overflow-y-auto">
          {drawer.item && (
            <>
              <SheetHeader>
                <SheetTitle>{drawer.item.name}</SheetTitle>
                <SheetDescription>{drawer.item.holders.length} employees have completed this training</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-2">
                {drawer.item.holders.map((h) => (
                  <div key={h.training_id} data-testid={`train-holder-${h.training_id}`} className={`rounded-2xl border p-3 ${h.status === "Expired" ? "border-rose-200 bg-rose-50/40 dark:border-rose-500/30 dark:bg-rose-500/10" : h.status === "Expiring" ? "border-amber-200 bg-amber-50/40 dark:border-amber-500/30 dark:bg-amber-500/10" : "border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40"}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{h.employee_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{h.position} · {h.client_name}</div>
                      </div>
                      <Badge variant="outline" className={`text-[10px] rounded-full shrink-0 ${TRAINING_TONE[h.status] || ""}`}>{h.status}</Badge>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[11px] font-mono text-slate-500">
                      <span>Issued {h.issue_date || "—"}</span>
                      <span>Expires {h.expiry_date || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      {h.has_certificate ? (
                        <Button asChild size="sm" variant="outline" data-testid={`train-cert-link-${h.training_id}`}>
                          <a href={endpoints.certificateUrl(h.employee_id, h.training_id)} target="_blank" rel="noreferrer"><FileText className="h-3.5 w-3.5 mr-1" /> Open certificate</a>
                        </Button>
                      ) : (
                        <span className="text-[10px] text-slate-500 italic">No certificate on file</span>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => { setDrawer({ open: false, item: null }); navigate(`/employees?client=${h.client_slug}`); }}>Open employee →</Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
